'use client';

import { Button } from '@/components/ui/button';
import { UserInfoContextType, useUserInfo } from '@/provider/UserInfoProvider';
import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useSignupModal } from '@/zustand/store';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { app } from '@/config/firebaseConfig';
import { buildUrlWithParams } from '@/utils/someUtils';
import { kazeFetch } from '@/utils/kazeFetch';
import { createSubscriptionEndpoint } from '@/config/endpoints';
import { Loader2 } from 'lucide-react';
import { type EventMap, posthogUTM, RPostHog } from '@/posthog';

interface MicroPlanCardProps {
  _from: EventMap['price_success']['pay_from_module'];
  userIdentity?: UserInfoContextType['userIdentity'];
  isModal?: boolean;
  onSelect?: () => void;
  cancelUrl?: string;
  successRedirect?: string;
}

const pdName = 'Onetime_tiny_pay_1credits';

const { utm } = posthogUTM();
export default function MicroPlanCard({
  _from,
  onSelect,
  userIdentity,
  isModal = true,
  cancelUrl,
  successRedirect,
}: MicroPlanCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { updateUserInfoAssets, isLoadingUser } = useUserInfo();
  const stripePromise = loadStripe(
    process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!
  );
  const setSignupModalOpen = useSignupModal((s) => s.setSignupModalOpen);

  /**
   * @description 订阅
   * @param isSkipLogin 是否跳过验证登录
   */
  const handleSubscribe = async (isSkipLogin = false) => {
    try {
      // 上报
      if (userIdentity === 3) {
        RPostHog('user_after_click_price_btn', {
          user_identity: userIdentity,
          btn_name: 'Buy now',
          btn_product_name: 'Just One Image',
          ...utm,
        });
      } else {
        userIdentity &&
          RPostHog('user_not_exist_after_click_price_btn', {
            user_identity: userIdentity,
            btn_name: 'Buy now',
            btn_product_name: 'Just One Image',
            ...utm,
          });
      }

      if (!isSkipLogin && !isLoadingUser && userIdentity !== 3)
        return setSignupModalOpen(true);

      setIsLoading(true);
      const auth = getAuth(app);
      await auth.authStateReady();
      const user = auth.currentUser;
      const userId = user?.uid;

      if (!user || user.uid !== userId)
        return toast.error('User authentication failed');

      const reqBody = JSON.stringify({
        product_name: pdName,
        user_id: userId,
        module: _from,
        cancel_url: buildUrlWithParams(
          cancelUrl || '/pricing',
          isModal
            ? { canceled: 'true', product_name: pdName, credit_amount: '1' }
            : {}
        ),
        success_redirect: buildUrlWithParams(
          successRedirect || '/pricing',
          isModal ? { payment_success: 'true' } : {}
        ),
      });

      const data = await kazeFetch(createSubscriptionEndpoint, {
        method: 'POST',
        body: reqBody,
        headers: { utm: `product_name=${pdName}` },
      });

      updateUserInfoAssets();

      const { session_id } = data;
      const stripe = await stripePromise;
      await stripe!.redirectToCheckout({
        sessionId: session_id,
      });
    } catch (error: any) {
      console.error('Payment Error:', error);

      if (error?.code === 'SUB_YEAR_TO_MONTH_ERROR') {
        toast.error(
          'Annual subscribers cannot switch to monthly plans. Please continue with an annual subscription or wait until your current subscription ends.'
        );
        setIsLoading(false);
        return;
      }

      const errorMessage = error?.message || 'Payment processing failed';

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className='cursor-pointer rounded-xl border border-gray-200'
      onClick={onSelect}
    >
      <div className='h-full rounded-[11px] bg-transparent p-6 @container/micro-plan-card'>
        <div className='flex flex-row items-center justify-start gap-4'>
          <h3 className='font-helvetica-medium text-[1.75rem] text-[#252933]'>
            Just One Image?
          </h3>
        </div>

        <div className='mt-4 flex flex-row items-center justify-between'>
          <div className='flex items-baseline'>
            <span className='font-helvetica-medium text-[2rem] text-[#252933]'>
              $1.99
            </span>
            <span className='font-helvetica-roman ml-1 text-[1.125rem] text-[#252933]'>
              / image
            </span>
          </div>
        </div>

        <h4 className='font-helvetica-medium mt-[1.125rem] text-base font-medium leading-[normal] text-[#252933] @[14.5rem]/micro-plan-card:tracking-[-0.06rem] @[18.5rem]/micro-plan-card:tracking-normal'>
          Pay Once. No subscription needed.
        </h4>

        <Button
          className={`mt-[1.375rem] w-full bg-[#2563EB] text-white hover:bg-[#2563EB]/90 ${isLoading ? 'pointer-events-none' : 'pointer-events-auto'}`}
          onClick={() =>
            userIdentity === 3
              ? handleSubscribe()
              : setSignupModalOpen(true, {
                  signupSuccess: () => handleSubscribe(true),
                })
          }
        >
          {isLoading ? (
            <Loader2 className='animate-spin' size={24} />
          ) : (
            'Buy Now'
          )}
        </Button>
      </div>
    </div>
  );
}
